<script lang="ts">
  import { onMount, onDestroy } from "svelte";

  function resolveGrapiBase(): string {
    if (typeof window !== "undefined") {
      const loc = window.location;
      if (loc.hostname.startsWith("acelinkhs-alhs-gras-gruim.")) {
        return `${loc.protocol}//${loc.hostname.replace("acelinkhs-alhs-gras-gruim.", "acelinkhs-alhs-gras-grapi.")}`;
      }
      if (loc.hostname.startsWith("acelinkhs-alhs.")) {
        return `${loc.protocol}//${loc.hostname.replace("acelinkhs-alhs.", "acelinkhs-alhs-gras-grapi.")}`;
      }
    }
    return "";
  }

  const GRAPI_BASE = resolveGrapiBase();
  const STATUS_URL = GRAPI_BASE ? `${GRAPI_BASE}/system-upgrade/status` : "/api/system-upgrade/status";
  const TRIGGER_URL = GRAPI_BASE ? `${GRAPI_BASE}/system-upgrade/trigger` : "/api/system-upgrade/trigger";

  interface UpgradeStatus {
    isPrd: boolean;
    upgradeAvailable: boolean;
    imagesVerified?: boolean;
    currentAlaceVersion?: string;
    currentAlhsVersion?: string;
    currentAlainVersion?: string;
    targetAlaceVersion?: string;
    targetAlhsVersion?: string;
    targetAlainVersion?: string;
    state?: "IDLE" | "IN_PROGRESS" | "COMPLETED" | "FAILED";
    step?: string;
    stepDescription?: string;
    updatedAt?: string;
    error?: string | null;
    reason?: string;
  }

  const clientStage = (
    process.env.SVELTE_APP_GRAPPLE_STAGE ||
    process.env.SVELTE_APP_STAGE ||
    ""
  ).toLowerCase();
  const isClientPrd = clientStage === "prd";

  let loading = true;
  let checking = false;
  let triggering = false;
  let showConfirmModal = false;
  let isFetchingStatus = false;
  let status: UpgradeStatus = {
    isPrd: isClientPrd,
    upgradeAvailable: false,
    state: "IDLE",
  };
  let pollInterval: any = null;

  async function fetchWithTimeout(url: string, options: RequestInit = {}, timeoutMs = 10000): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timer);
    }
  }

  async function fetchStatus(isManualCheck = false) {
    if (isFetchingStatus) return;
    isFetchingStatus = true;
    if (isManualCheck) checking = true;
    try {
      const cacheBuster = `_t=${Date.now()}`;
      const refreshParam = isManualCheck ? "&refresh=1" : "";
      const urlWithBuster = (url: string) => url.includes("?") ? `${url}&${cacheBuster}${refreshParam}` : `${url}?${cacheBuster}${refreshParam}`;

      let res = await fetchWithTimeout(urlWithBuster(STATUS_URL), {
        cache: "no-store",
        headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" }
      }, 10000);
      if (!res.ok) {
        const altUrl = STATUS_URL.includes("/api/")
          ? STATUS_URL.replace("/api/", "/")
          : STATUS_URL.replace("/system-upgrade/", "/api/system-upgrade/");
        const altRes = await fetchWithTimeout(urlWithBuster(altUrl), {
          cache: "no-store",
          headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" }
        }, 10000);
        if (altRes.ok) {
          res = altRes;
        }
      }
      if (res.ok) {
        const text = await res.text();
        if (text && text.trim().startsWith("{")) {
          const data = JSON.parse(text);
          status = {
            ...data,
            isPrd: data.isPrd || isClientPrd,
          };
          if (status.state === "COMPLETED" || status.state === "FAILED") {
            try { sessionStorage.removeItem("app:upgrade_in_progress"); } catch (_) {}
          }
        }
      } else if (isClientPrd) {
        status.isPrd = true;
      }
    } catch (e: any) {
      if (e?.name === "AbortError") {
        console.warn("[UpgradeManager] Status request timed out (>10s); will retry on next poll cycle.");
        if (status.state === "IN_PROGRESS") {
          status.stepDescription = "Waiting for service pod to finish restart and reconnect...";
        }
      } else {
        console.error("Failed to load upgrade status:", e);
      }
      if (isClientPrd) {
        status.isPrd = true;
      }
    } finally {
      loading = false;
      checking = false;
      isFetchingStatus = false;
    }
  }

  function startPolling() {
    stopPolling();
    pollInterval = setInterval(async () => {
      await fetchStatus();
      if (status.state === "COMPLETED" || status.state === "FAILED") {
        stopPolling();
      }
    }, 4000);
  }

  function stopPolling() {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  }

  async function triggerUpgrade() {
    triggering = true;
    showConfirmModal = false;
    try {
      const payload = JSON.stringify({
        targetAlaceVersion: status.targetAlaceVersion,
        targetAlhsVersion: status.targetAlhsVersion,
        targetAlainVersion: status.targetAlainVersion,
      });
      let res = await fetchWithTimeout(TRIGGER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: payload,
      }, 15000);
      if (!res.ok) {
        const altUrl = TRIGGER_URL.includes("/api/")
          ? TRIGGER_URL.replace("/api/", "/")
          : TRIGGER_URL.replace("/system-upgrade/", "/api/system-upgrade/");
        const altRes = await fetchWithTimeout(altUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: payload,
        }, 15000);
        if (altRes.ok) {
          res = altRes;
        }
      }
      if (res.ok) {
        status.state = "IN_PROGRESS";
        status.step = "ROLLING_OUT_PODS";
        status.stepDescription = "In-cluster Helm upgrade Job submitted. Upgrading components in background...";
        try { sessionStorage.setItem("app:upgrade_in_progress", "true"); } catch (_) {}
        startPolling();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(`Failed to trigger upgrade: ${data.message || res.statusText}`);
      }
    } catch (err: any) {
      alert(`Network error triggering upgrade: ${err.message}`);
    } finally {
      triggering = false;
    }
  }

  function reloadApp() {
    window.location.reload();
  }

  onMount(async () => {
    try {
      if (typeof window !== "undefined" && sessionStorage.getItem("app:upgrade_in_progress") === "true") {
        status.state = "IN_PROGRESS";
        status.stepDescription = "Waiting for service pod to finish restart and reconnect...";
        startPolling();
      }
    } catch (_) {}
    await fetchStatus();
    if (status.state === "IN_PROGRESS") {
      startPolling();
    }
  });

  onDestroy(() => {
    stopPolling();
  });
</script>

<div class="rounded-2xl border border-slate-700/60 bg-[#16192b]/80 backdrop-blur-md p-6 shadow-xl">
  <!-- Header -->
  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-700/60 pb-5">
    <div>
      <div class="flex items-center gap-2">
        <h2 class="text-xl font-bold tracking-tight text-white">Production Upgrade Center</h2>
        <span class="inline-flex items-center rounded-md bg-indigo-500/10 px-2 py-0.5 text-xs font-medium text-indigo-400 ring-1 ring-inset ring-indigo-500/20">
          PRD Only
        </span>
      </div>
      <p class="mt-1 text-xs text-slate-400">
        In-cluster automated rolling upgrade manager for ALACE, ALHS, and ALAIN components.
      </p>
    </div>

    <!-- Manual Refresh / Action Button -->
    <div class="flex items-center gap-3">
      <button
        type="button"
        on:click={() => fetchStatus(true)}
        disabled={checking || loading || status.state === "IN_PROGRESS"}
        class="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800/80 px-3.5 py-2 text-xs font-medium text-slate-300 shadow-sm hover:bg-slate-700 hover:text-white disabled:opacity-50 transition-colors"
      >
        <svg
          class="h-3.5 w-3.5 {checking ? 'animate-spin text-indigo-400' : 'text-slate-400'}"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        <span>{checking ? "Checking ECR & Hub..." : "Check for Updates"}</span>
      </button>
    </div>
  </div>

  {#if loading}
    <div class="flex flex-col items-center justify-center py-12 text-slate-400">
      <svg class="h-8 w-8 animate-spin text-indigo-500" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
      </svg>
      <span class="mt-3 text-xs font-medium">Checking cluster upgrade status...</span>
    </div>
  {:else if !status.isPrd}
    <!-- Restricted Non-PRD View -->
    <div class="mt-6 rounded-xl border border-amber-500/20 bg-amber-950/20 p-5 text-sm text-amber-200">
      <div class="flex items-start gap-3">
        <svg class="h-5 w-5 text-amber-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div>
          <p class="font-semibold text-amber-200">Self-Service Upgrade Restricted</p>
          <p class="mt-1 text-slate-300">
            Automated in-app upgrades are only permitted in Production (PRD) stacks. In Sandbox or UAT environments, upgrades must be performed via deployment pipelines.
          </p>
        </div>
      </div>
    </div>
  {:else}
    <!-- PRD Active View -->
    <div class="mt-6 space-y-6">
      <!-- Current Version Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="rounded-lg bg-slate-800/50 p-4 border border-slate-700/40">
          <div class="text-xs font-semibold uppercase tracking-wider text-slate-400">ALACE Chart Version</div>
          <div class="mt-2 flex items-baseline gap-2">
            <span class="text-2xl font-bold text-white">{status.currentAlaceVersion || "0.2"}</span>
            {#if status.targetAlaceVersion && status.targetAlaceVersion !== status.currentAlaceVersion}
              <span class="text-xs text-indigo-400 font-medium">➔ New: {status.targetAlaceVersion}</span>
            {/if}
          </div>
        </div>

        <div class="rounded-lg bg-slate-800/50 p-4 border border-slate-700/40">
          <div class="text-xs font-semibold uppercase tracking-wider text-slate-400">ALHS Chart Version</div>
          <div class="mt-2 flex items-baseline gap-2">
            <span class="text-2xl font-bold text-white">{status.currentAlhsVersion || "0.2"}</span>
            {#if status.targetAlhsVersion && status.targetAlhsVersion !== status.currentAlhsVersion}
              <span class="text-xs text-indigo-400 font-medium">➔ New: {status.targetAlhsVersion}</span>
            {/if}
          </div>
        </div>

        <div class="rounded-lg bg-slate-800/50 p-4 border border-slate-700/40">
          <div class="text-xs font-semibold uppercase tracking-wider text-slate-400">ALAIN Chart Version</div>
          <div class="mt-2 flex items-baseline gap-2">
            <span class="text-2xl font-bold text-white">{status.currentAlainVersion || "0.1"}</span>
            {#if status.targetAlainVersion && status.targetAlainVersion !== status.currentAlainVersion}
              <span class="text-xs text-indigo-400 font-medium">➔ New: {status.targetAlainVersion}</span>
            {/if}
          </div>
        </div>
      </div>

      <!-- State: IN_PROGRESS -->
      {#if status.state === "IN_PROGRESS"}
        <div class="rounded-xl border border-indigo-500/30 bg-indigo-950/20 p-6">
          <div class="flex items-center gap-3">
            <div class="relative flex h-5 w-5">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-5 w-5 bg-indigo-500"></span>
            </div>
            <div>
              <h3 class="text-base font-bold text-white">Upgrade in Progress</h3>
              <p class="text-xs text-indigo-300">
                Target: ALACE {status.targetAlaceVersion || "Current"} | ALHS {status.targetAlhsVersion || "Current"} | ALAIN {status.targetAlainVersion || "Current"}
              </p>
            </div>
          </div>

          <div class="mt-5 rounded-lg bg-slate-900/60 p-4 border border-slate-700/50">
            <div class="flex items-center justify-between text-xs font-medium text-slate-400 mb-2">
              <span>Current Phase:</span>
              <span class="text-indigo-400 font-mono">{status.step || "ROLLING_OUT"}</span>
            </div>
            <p class="text-sm font-semibold text-slate-200">{status.stepDescription || "Running Helm upgrade..."}</p>
            
            <div class="mt-4 flex items-center gap-2 text-xs text-slate-400">
              <svg class="h-4 w-4 animate-spin text-indigo-400" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
              </svg>
              <span>Waiting for microservice pods to restart and reach 100% Ready status...</span>
            </div>
          </div>
        </div>

      <!-- Upgrade Available Banner -->
      {:else if status.upgradeAvailable}
        <div class="rounded-xl border border-indigo-500/30 bg-gradient-to-r from-indigo-900/40 via-purple-900/20 to-slate-900/40 p-6 shadow-lg">
          <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <div class="inline-flex items-center gap-1.5 rounded-full bg-indigo-500/20 px-2.5 py-0.5 text-xs font-semibold text-indigo-300">
                🚀 New Production Release Available
              </div>
              <h3 class="mt-2 text-lg font-bold text-white">
                Upgrade Release: ALACE {status.targetAlaceVersion || status.currentAlaceVersion} / ALHS {status.targetAlhsVersion || status.currentAlhsVersion} / ALAIN {status.targetAlainVersion || status.currentAlainVersion}
              </h3>
              <p class="mt-1 text-xs text-slate-300">
                Verified: Chart releases match valid SemVer (<span class="font-mono">&lt;0-99&gt;.&lt;0-99&gt;.&lt;0-99&gt;</span>) and container images exist on Docker Hub.
              </p>
              
              <div class="mt-3 flex items-center gap-2 text-xs font-semibold text-emerald-400">
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>Docker Hub container images verified</span>
              </div>
            </div>

            <button
              type="button"
              on:click={() => (showConfirmModal = true)}
              class="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-indigo-600/30 hover:bg-indigo-500 transition-all hover:scale-[1.02]"
            >
              <span>Upgrade Now</span>
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>

      <!-- State: COMPLETED -->
      {:else if status.state === "COMPLETED"}
        <div class="rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-6">
          <div class="flex items-center gap-3">
            <div class="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 class="text-base font-bold text-emerald-300">Upgrade Completed Successfully!</h3>
              <p class="text-xs text-slate-300">All microservice pods are running, healthy, and responsive.</p>
            </div>
          </div>

          <div class="mt-4 flex items-center gap-3">
            <button
              type="button"
              on:click={reloadApp}
              class="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 transition-colors shadow-lg shadow-emerald-900/30"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reload Application
            </button>
          </div>
        </div>

      <!-- State: FAILED -->
      {:else if status.state === "FAILED"}
        <div class="rounded-xl border border-red-500/30 bg-red-950/20 p-6">
          <div class="flex items-center gap-3">
            <div class="flex h-9 w-9 items-center justify-center rounded-full bg-red-500/20 text-red-400">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div>
              <h3 class="text-base font-bold text-red-300">Upgrade Encountered an Error</h3>
              <p class="text-xs text-slate-300">{status.error || status.stepDescription || "Pod rollout timed out or deployment failed."}</p>
            </div>
          </div>
          
          <div class="mt-4">
            <button
              type="button"
              on:click={() => fetchStatus(true)}
              class="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-300 hover:bg-slate-700"
            >
              Dismiss / Re-check
            </button>
          </div>
        </div>

      {:else}
        <!-- Up to Date -->
        <div class="flex items-center gap-3 rounded-lg bg-slate-800/40 p-4 border border-slate-700/30">
          <div class="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-400">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p class="text-sm font-semibold text-slate-200">Application is up to date</p>
            <p class="text-xs text-slate-400">No newer production releases found matching format &lt;0-99&gt;.&lt;0-99&gt;.&lt;0-99&gt;.</p>
          </div>
        </div>
      {/if}
    </div>
  {/if}
</div>

<!-- Confirmation Modal -->
{#if showConfirmModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
    <div class="w-full max-w-md rounded-2xl border border-slate-700 bg-[#1e2238] p-6 shadow-2xl">
      <div class="flex items-center gap-3 text-amber-400">
        <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h3 class="text-lg font-bold text-white">Confirm Cluster Upgrade</h3>
      </div>

      <p class="mt-3 text-sm text-slate-300">
        You are about to upgrade the production cluster:
      </p>

      <div class="mt-3 rounded-lg bg-slate-900/60 p-3 text-xs font-mono space-y-1 text-slate-200 border border-slate-700/50">
        <div>• ALACE: {status.currentAlaceVersion} ➔ <span class="text-indigo-400 font-bold">{status.targetAlaceVersion || status.currentAlaceVersion}</span></div>
        <div>• ALHS:  {status.currentAlhsVersion} ➔ <span class="text-indigo-400 font-bold">{status.targetAlhsVersion || status.currentAlhsVersion}</span></div>
        {#if status.targetAlainVersion}
          <div>• ALAIN: {status.currentAlainVersion} ➔ <span class="text-indigo-400 font-bold">{status.targetAlainVersion}</span></div>
        {/if}
      </div>

      <p class="mt-3 text-xs text-slate-400 leading-relaxed">
        This will run an in-cluster Helm upgrade to deploy new charts and roll out microservice pods. The UI will show a live status until all pods are confirmed 100% Ready.
      </p>

      <div class="mt-6 flex justify-end gap-3">
        <button
          type="button"
          on:click={() => (showConfirmModal = false)}
          disabled={triggering}
          class="rounded-lg bg-slate-800 px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-700"
        >
          Cancel
        </button>
        <button
          type="button"
          on:click={triggerUpgrade}
          disabled={triggering}
          class="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-500 shadow-lg shadow-indigo-600/30"
        >
          {triggering ? "Initiating..." : "Confirm & Proceed"}
        </button>
      </div>
    </div>
  </div>
{/if}
